/**
 * AvatarImage - Model avatar with loading and error states.
 */

import { useState, useRef, useCallback } from 'react';
import { ImageOff } from 'lucide-react';
import { buildApiUrl } from '@/services/urlResolver';
import { isLocalMode, isElectronMode } from '@/utils/apiMode';
import { getAvatarForModel } from './modelAvatars';

// Avatar API endpoint
const AVATAR_API_ENDPOINT = '/api/v1/llm/avatars';

export function AvatarImage({
  modelId,
  size = 'md',
  className = '',
  onHover,
  onLeave,
}: {
  modelId: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onHover?: (rect: DOMRect) => void;
  onLeave?: () => void;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Subdirectory avatars only exist in cloud (GCS), use flat filenames locally
  const isLocal = isLocalMode() || isElectronMode();
  const avatarFile = getAvatarForModel(modelId, !isLocal);
  const avatarUrl = buildApiUrl(`${AVATAR_API_ENDPOINT}/${avatarFile}`);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  const handleMouseEnter = useCallback(() => {
    if (imgRef.current && onHover) {
      const rect = imgRef.current.getBoundingClientRect();
      onHover(rect);
    }
  }, [onHover]);

  if (hasError) {
    return (
      <div
        className={`${sizeClasses[size]} rounded-full bg-white/10 flex items-center justify-center ${className}`}
      >
        <ImageOff className="w-4 h-4 text-white/40" />
      </div>
    );
  }

  return (
    <div
      ref={imgRef}
      className={`${sizeClasses[size]} rounded-full overflow-hidden relative ${className} cursor-pointer`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onLeave}
    >
      {isLoading && <div className="absolute inset-0 bg-white/10 animate-pulse rounded-full" />}
      <img
        src={avatarUrl}
        alt={modelId}
        className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
        loading="lazy"
      />
    </div>
  );
}

export default AvatarImage;
